import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Moment from 'react-moment'
import DateTimePicker from 'react-datetime-picker'

// This import loads the firebase namespace along with all its type information.
import firebase from 'firebase/compat/app'

// These imports load individual services into the firebase namespace.
import 'firebase/compat/auth'
import 'firebase/compat/firestore'

let firebaseApp
let lastSeenDocument = {}

const MobileAdAnalytics = ({ adId, site }) => {
    let { id: siteId } = useParams()
    const [analytics, setAnalytics] = useState([])
    const [isLoading, setLoading] = useState(false)
    const [notSetup, setNotSetup] = useState(false)
    const [stats, setStats] = useState({})
    const [filter, setFilter] = useState({
        start_date: null,
        end_date: null,
    })

    useEffect(() => {
        let app

        if (site.firebaseConfig) {
            app = firebase.initializeApp(
                {
                    ...site.firebaseConfig,
                },
                siteId
            )

            firebaseApp = app

            return () => {
                app.delete()
                firebaseApp = {}
            }
        } else {
            setNotSetup(true)
        }
    }, [siteId])

    useEffect(() => {
        if (firebaseApp) {
            console.log(`fetch ads for ${adId}...`)

            fetchStats(adId).then((document) => {
                setStats(document)
            })

            fetchAnalytics(adId).then((results) => {
                setAnalytics(results)
                if (results && results.length > 0) {
                    lastSeenDocument = results[results.length - 1].doc
                }
            })
        }
    }, [adId])

    const fetchStats = async (adId) => {
        try {
            /**
             * Fetch data from firestore
             */
            const ad_stats = await firebaseApp
                .firestore()
                .collection('stats')
                .doc(adId)
                .get()

            /**
             * Return document
             */
            if (!ad_stats.exists) {
                throw `Stats haven't been processed yet`
            } else {
                return ad_stats.data()
            }
        } catch (err) {
            console.log(err)
        }
    }

    const fetchAnalytics = async (adId) => {
        try {
            /**
             * Fetch data from firestore
             */
            const analytics = await firebaseApp
                .firestore()
                .collection('events')
                .where('ad_id', '==', adId)
                .orderBy('created_at', 'desc')
                .limit(25)
                .get()

            /**
             * Return a new array of the data in a format we like
             */
            return analytics.docs.map((doc) => {
                return {
                    id: doc.id,
                    doc: doc,
                    ...doc.data(),
                }
            })
        } catch (err) {
            console.log(err)
            alert('something went wrong getting analytics')
        }
    }

    const handleFilterUpdate = () => {
        fetchAnalyticsByDate(adId, filter.start_date, filter.end_date).then(
            (results) => {
                console.log('results!')
                console.log(results)
                setAnalytics(results)
                if (results && results.length > 0) {
                    lastSeenDocument = results[results.length - 1].doc
                }
            }
        )
    }

    const fetchAnalyticsByDate = async (adId, startDate, endDate) => {
        try {
            /**
             * Fetch data from firestore
             */
            const analytics = await firebaseApp
                .firestore()
                .collection('events')
                .where('ad_id', '==', adId)
                .where('created_at', '>=', startDate)
                .where('created_at', '<=', endDate)
                .orderBy('created_at', 'desc')
                .get()

            /**
             * Return a new array of the data in a format we like
             */
            return analytics.docs.map((doc) => {
                return {
                    id: doc.id,
                    doc: doc,
                    ...doc.data(),
                }
            })
        } catch (err) {
            console.log(err)
            alert('something went wrong getting analytics')
        }
    }

    const handleLoadMore = () => {
        if (firebaseApp) {
            loadMoreAnalytics().then((results) => {
                setAnalytics([...analytics, ...results])
                if (results.length > 0) {
                    lastSeenDocument = results[results.length - 1].doc
                } else {
                    alert('You have reached the end of the current analytics')
                }
            })
        }
    }

    const loadMoreAnalytics = async () => {
        try {
            /**
             * Fetch data from firestore
             */
            console.log('loading more analytics')
            console.log(lastSeenDocument)
            const analytics = await firebaseApp
                .firestore()
                .collection('events')
                .where('ad_id', '==', adId)
                .orderBy('created_at', 'desc')
                .startAfter(lastSeenDocument)
                .limit(25)
                .get()

            /**
             * Return a new array of the data in a format we like
             */
            return analytics.docs.map((doc) => {
                return {
                    id: doc.id,
                    doc: doc,
                    ...doc.data(),
                }
            })
        } catch (err) {
            console.log(err)
            alert('Something went wrong loading more analytics')
        }
    }

    const getClicksForPlatform = (platform) => {
        return analytics.filter((item) => item.platform === platform).length
    }

    const hideDateFilter = () => {
        if (!filter.start_date && !filter.end_date) {
            return true
        } else {
            return false
        }
    }

    const enableDateFilter = () => {
        /**
         * Sets both filters on enable to current date
         */
        setAnalytics([])
        setFilter({
            start_date: new Date(),
            end_date: new Date(),
        })
    }

    if (notSetup) {
        return <div>Analytics are not setup yet</div>
    }

    return (
        <div>
            <div className="mb-12">
                <div className="border-b-2 border-gray-300 pb-5">
                    <div className="text-gray-700 text-sm flex flex-row items-center mb-3">
                        <img
                            src="/filter.svg"
                            className="w-4 h-4 opacity-75 mr-2"
                        />
                        Filter by date
                    </div>
                    {hideDateFilter() && (
                        <button
                            onClick={() => enableDateFilter()}
                            className="bg-gray-200 border-2 border-gray-400 border-dashed px-12 py-3 hover:border-gray-700 rounded focus:outline-none text-gray-800"
                        >
                            Set date filter
                        </button>
                    )}
                    <div
                        className={`grid grid-cols-8 gap-4 ${
                            hideDateFilter() ? 'hidden' : ''
                        }`}
                    >
                        <div className="col-span-2 flex flex-col">
                            <label className="uppercase text-sm tracking-wide font-semibold text-gray-700 ml-2 mb-px">
                                Start Date
                            </label>
                            <DateTimePicker
                                className="inline-block bg-white border-2 border-gray-300 focus:border-blue-500 px-3 py-2 rounded-lg outline-none"
                                onChange={(e) =>
                                    setFilter({
                                        ...filter,
                                        start_date: e,
                                    })
                                }
                                value={
                                    filter.start_date
                                        ? new Date(filter.start_date)
                                        : new Date()
                                }
                            />
                        </div>
                        <div className="col-span-2 flex flex-col">
                            <label className="uppercase text-sm tracking-wide font-semibold text-gray-700 ml-2 mb-px">
                                End Date
                            </label>
                            <DateTimePicker
                                className="inline-block bg-white border-2 border-gray-300 focus:border-blue-500 px-3 py-2 rounded-lg outline-none"
                                onChange={(e) =>
                                    setFilter({
                                        ...filter,
                                        end_date: e,
                                    })
                                }
                                value={
                                    filter.end_date
                                        ? new Date(filter.end_date)
                                        : new Date()
                                }
                            />
                        </div>
                        <div className="col-span-3 flex flex-col justify-start items-end grid grid-cols-6 gap-2">
                            <button
                                onClick={() => handleFilterUpdate()}
                                className="col-span-2 bg-green-500 text-white text-center px-5 py-3 focus:outline-none hover:bg-green-400 rounded-lg"
                            >
                                Submit
                            </button>
                            <button
                                onClick={() =>
                                    fetchAnalytics(adId).then((results) => {
                                        setAnalytics(results)
                                        if (results && results.length > 0) {
                                            lastSeenDocument =
                                                results[results.length - 1].doc
                                        }
                                        setFilter({
                                            start_date: null,
                                            end_date: null,
                                        })
                                    })
                                }
                                className="col-span-3 bg-gray-300 text-gray-800 text-center px-5 py-3 focus:outline-none hover:bg-gray-400 rounded-lg"
                            >
                                Reset to default
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {filter.start_date && filter.end_date && (
                <FilterAnalyticItems analytics={analytics} />
            )}
            {stats &&
                stats.total_clicks &&
                !filter.start_date &&
                !filter.end_date && (
                    <div className="mb-12 grid grid-cols-3 gap-4">
                        <div className="bg-white p-4 rounded shadow">
                            <div className="font-black text-xl">
                                {stats.total_clicks}
                            </div>
                            <div>Total Clicks</div>
                        </div>
                        <div className="bg-white p-4 rounded shadow relative overflow-hidden">
                            <div className="font-black text-xl">
                                {stats.ios_clicks}
                            </div>
                            <div>
                                iOS Clicks (
                                {Math.round(
                                    (stats.ios_clicks * 100) /
                                        stats.total_clicks
                                )}
                                %)
                            </div>
                            <div className="w-32 absolute top-0 right-0 transform rotate-12 opacity-25">
                                <img src="/apple.svg" />
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded shadow relative overflow-hidden">
                            <div className="font-black text-xl">
                                {stats.android_clicks}
                            </div>
                            <div>
                                Android Clicks (
                                {Math.round(
                                    (stats.android_clicks * 100) /
                                        stats.total_clicks
                                )}
                                %)
                            </div>
                            <div className="w-32 absolute top-0 right-0 transform rotate-12 opacity-50">
                                <img src="/android.svg" />
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded shadow relative overflow-hidden">
                            <div className="font-black text-xl">
                                {stats.home_screen_clicks}
                            </div>
                            <div>
                                Home Screen Clicks (
                                {Math.round(
                                    (stats.home_screen_clicks * 100) /
                                        stats.total_clicks
                                )}
                                %)
                            </div>
                            <div className="w-32 absolute top-0 right-0 transform rotate-12 opacity-50"></div>
                        </div>
                        <div className="bg-white p-4 rounded shadow relative overflow-hidden">
                            <div className="font-black text-xl">
                                {stats.post_screen_clicks}
                            </div>
                            <div>
                                Post Screen Clicks (
                                {Math.round(
                                    (stats.post_screen_clicks * 100) /
                                        stats.total_clicks
                                )}
                                %)
                            </div>
                            <div className="w-32 absolute top-0 right-0 transform rotate-12 opacity-50"></div>
                        </div>
                        <div className="bg-white p-4 rounded shadow relative overflow-hidden">
                            <div className="font-black text-xl">
                                {stats.category_screen_clicks}
                            </div>
                            <div>
                                Category Screen Clicks (
                                {Math.round(
                                    (stats.category_screen_clicks * 100) /
                                        stats.total_clicks
                                )}
                                %)
                            </div>
                            <div className="w-32 absolute top-0 right-0 transform rotate-12 opacity-50"></div>
                        </div>
                        <div className="col-span-3 text-gray-700 text-sm flex flex-row items-center">
                            <img
                                src="/clock.svg"
                                className="w-4 h-4 opacity-75 mr-2"
                            />
                            Stats last generated on{' '}
                            {new Date(stats.generated_on).toDateString()}
                        </div>
                    </div>
                )}
            <div>
                <p className="text-2xl font-light text-gray-900 mb-4">
                    Most recent events
                </p>
            </div>
            <div className="bg-white rounded shadow px-4 pt-4 overflow-hidden mb-8">
                <div className="flex items-center justify-between pb-4 font-bold">
                    <div className="w-1/4">Created At</div>
                    <div className="w-1/6">Platform</div>
                    <div className="w-1/6">Type</div>
                    <div className="w-1/6">Screen</div>
                    <div className="w-1/4">Link</div>
                </div>
                <div>
                    {analytics &&
                        analytics.length > 0 &&
                        analytics.map((item) => {
                            return <AnalyticItem key={item.id} item={item} />
                        })}
                </div>
            </div>
            <div className="mb-24">
                <button
                    onClick={() => handleLoadMore()}
                    className="rounded bg-blue-500 text-white px-4 py-2 hover:bg-blue-600 cursor-pointer"
                >
                    Load More Analytics
                </button>
            </div>
        </div>
    )
}

const AnalyticItem = ({ item }) => {
    const toDateTime = (secs) => {
        var t = new Date(1970, 0, 1) // Epoch
        t.setSeconds(secs)
        return t
    }

    const formatLinkType = (type) => {
        switch (type) {
            case 'tel:': {
                return 'Phone call'
            }
            case 'https://': {
                return 'Web Page'
            }
            case 'mailto:': {
                return 'Email'
            }
        }
    }

    return (
        <div className="flex items-center justify-between pb-4">
            <div className="w-1/4">
                <Moment format="ll">
                    {toDateTime(item.created_at.seconds)}
                </Moment>
            </div>
            <div className="w-1/6">{item.platform}</div>
            <div className="w-1/6">{formatLinkType(item.link_type)}</div>
            <div className="w-1/6 capitalize">
                {item.screen ? item.screen : 'N/A'}
            </div>
            <div className="w-1/4 flex items-center text-left relative overflow-hidden">
                <a
                    href={`${
                        item.link_type === 'https://'
                            ? 'https://' + item.link
                            : '#'
                    }`}
                    target={`${
                        item.link_type === 'https://' ? '_blank' : '_self'
                    }`}
                    className={`${
                        item.link_type === 'https://'
                            ? 'text-gray-700 hover:text-black underline'
                            : 'text-gray-900 cursor-text'
                    }`}
                >
                    {item.link}
                </a>
                <div className="absolute w-24 h-full bg-gradient-to-r from-transparent to-white top-0 right-0"></div>
            </div>
        </div>
    )
}

const FilterAnalyticItems = ({ analytics = [] }) => {
    return (
        <div className="mb-12 grid grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded shadow">
                <div className="font-black text-xl">{analytics.length}</div>
                <div>Total Clicks</div>
            </div>
            <div className="bg-white p-4 rounded shadow relative overflow-hidden">
                <div className="font-black text-xl">
                    {analytics.filter((item) => item.platform === 'ios').length}
                </div>
                <div>
                    iOS Clicks (
                    {Math.round(
                        (analytics.filter((item) => item.platform === 'ios')
                            .length *
                            100) /
                            analytics.length
                    )}
                    %)
                </div>
                <div className="w-32 absolute top-0 right-0 transform rotate-12 opacity-25">
                    <img src="/apple.svg" />
                </div>
            </div>
            <div className="bg-white p-4 rounded shadow relative overflow-hidden">
                <div className="font-black text-xl">
                    {
                        analytics.filter((item) => item.platform === 'android')
                            .length
                    }
                </div>
                <div>
                    Android Clicks (
                    {Math.round(
                        (analytics.filter((item) => item.platform === 'android')
                            .length *
                            100) /
                            analytics.length
                    )}
                    %)
                </div>
                <div className="w-32 absolute top-0 right-0 transform rotate-12 opacity-50">
                    <img src="/android.svg" />
                </div>
            </div>
            <div className="bg-white p-4 rounded shadow relative overflow-hidden">
                <div className="font-black text-xl">
                    {analytics.filter((item) => item.screen === 'Home').length}
                </div>
                <div>
                    Home Screen Clicks (
                    {Math.round(
                        (analytics.filter((item) => item.screen === 'Home')
                            .length *
                            100) /
                            analytics.length
                    )}
                    %)
                </div>
                <div className="w-32 absolute top-0 right-0 transform rotate-12 opacity-50"></div>
            </div>
            <div className="bg-white p-4 rounded shadow relative overflow-hidden">
                <div className="font-black text-xl">
                    {analytics.filter((item) => item.screen === 'Post').length}
                </div>
                <div>
                    Post Screen Clicks (
                    {Math.round(
                        (analytics.filter((item) => item.screen === 'Post')
                            .length *
                            100) /
                            analytics.length
                    )}
                    %)
                </div>
                <div className="w-32 absolute top-0 right-0 transform rotate-12 opacity-50"></div>
            </div>
            <div className="bg-white p-4 rounded shadow relative overflow-hidden">
                <div className="font-black text-xl">
                    {
                        analytics.filter((item) => item.screen === 'Category')
                            .length
                    }
                </div>
                <div>
                    Category Screen Clicks (
                    {Math.round(
                        (analytics.filter((item) => item.screen === 'Category')
                            .length *
                            100) /
                            analytics.length
                    )}
                    %)
                </div>
                <div className="w-32 absolute top-0 right-0 transform rotate-12 opacity-50"></div>
            </div>
        </div>
    )
}

export default MobileAdAnalytics
